import {
  ClassSchedule,
  ExamSessionDetails,
} from '../../../../../../../../api/types/programs/course/exams/response/exam-metadata';
import globalCourseStore from '../../../../../../../../stores/global-course-store';
import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RequestEnrollment } from '../../../../../../../../api/types/programs/course/exams/requests/request-exam-enrollment-update';
import { EnrollmentWithUserDb } from '../../../../../../../../api/types/programs/course/exams/exam-sessions-enrollment-entity';
import globalProgramStore from '../../../../../../../../stores/program-store';
import { upsertStudentsEnrollmentOperations } from '../../componenets/students-enrollment/operations/upsert-students-enrollment-operations';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { UserSummaryType } from '../../../../../../../../api/types/user/user-summary.type';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { AttendanceType } from '../../../../../../../../api/types/lecturers/attendance/attendance-type';

interface ExamPageAttendanceTabDatamodel {
  examId: number;
  examSession: ExamSessionDetails | null;
  setSessionDetails: (sessions: ExamSessionDetails[]) => any;
}

const columnStyle: CSSProperties = {
  textAlign: 'right',
  minWidth: '30px',
  maxWidth: '30px',
};

export const ExamPageAttendanceTab: React.FC<
  ExamPageAttendanceTabDatamodel
> = ({ examId, examSession, setSessionDetails }) => {
  const currentCourse = globalCourseStore.getCurrentCourse();
  const [loading, setLoading] = useState(false);

  const [studentClassRegistrations, setStudentClassRegistrations] = useState<
    Record<number, RequestEnrollment>
  >({});

  const initialEnrollmentState = useRef<Record<number, RequestEnrollment>>({});

  const initializeStudentRegistrations = (
    examSession: ExamSessionDetails,
  ): Record<number, RequestEnrollment> => {
    const initialRegistrations: Record<number, RequestEnrollment> = {};

    examSession.classesSchedule.forEach((classSchedule: ClassSchedule) => {
      classSchedule.enrollment.forEach((enrollment: EnrollmentWithUserDb) => {
        initialRegistrations[enrollment.studentId] = {
          studentId: enrollment.studentId,
          scheduleId: enrollment.scheduleId,
          attended: enrollment.attended,
        };
      });
    });

    return initialRegistrations;
  };

  useEffect(() => {
    if (examSession) {
      const initialState = initializeStudentRegistrations(examSession);
      initialEnrollmentState.current = initialState; // Store for comparison
      setStudentClassRegistrations(initialState); // Update current state
    }
  }, [examSession]);

  const hasChanges = useMemo(() => {
    const initialKeys = Object.keys(initialEnrollmentState.current);
    const currentKeys = Object.keys(studentClassRegistrations);

    if (initialKeys.length !== currentKeys.length) return true;

    for (const key of currentKeys) {
      const studentId = Number(key);
      const initial = initialEnrollmentState.current[studentId];
      const current = studentClassRegistrations[studentId];

      if (!initial) return true;

      if (
        initial.attended !== current.attended ||
        initial.scheduleId !== current.scheduleId
      ) {
        return true;
      }
    }

    return false;
  }, [studentClassRegistrations]);

  const updateStudentAttendance = (
    studentId: number,
    attendance: AttendanceType,
  ) => {
    setStudentClassRegistrations((prev) => {
      const newRegistrations = { ...prev };

      // If there's no existing registration for this student, create one
      if (!newRegistrations[studentId]) {
        newRegistrations[studentId] = {
          studentId,
          attended: attendance,
        };
      } else {
        // Update existing registration
        newRegistrations[studentId] = {
          ...newRegistrations[studentId],
          attended: attendance,
        };
      }

      return newRegistrations;
    });
  };

  const registerUsersEnrollment =
    examSession?.studentsRegistrations.filter((item) => item.enrollment) ?? [];
  const registerUsersEnrollmentSet = new Set(
    registerUsersEnrollment.map((item) => item.studentId),
  );

  const registerUser = currentCourse?.details?.students.filter((item) =>
    registerUsersEnrollmentSet.has(item.id),
  );

  const handleOnApprove = async () => {
    const currentProgramId = globalProgramStore.getCurrentId();

    await upsertStudentsEnrollmentOperations(
      currentProgramId,
      currentCourse?.metadata.id,
      examId,
      examSession?.id,
      initialEnrollmentState.current,
      studentClassRegistrations,
      setSessionDetails,
      setLoading,
    );
  };

  return (
    <div>
      <div>
        <DataTable
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
          selectionMode={'multiple'}
          multiple={true}
          groupRowsBy="dsf"
          value={registerUser}
          scrollable
          onSelectionChange={(e) => {
            //setSelectedUsers(e.value as UserSummaryType[]);
          }}
          dataKey="id"
          tableStyle={{
            direction: 'rtl',
            marginTop: '16px',
          }}
        >
          <Column
            expander={false}
            header="שם"
            field={'firstName'}
            style={columnStyle}
            body={(rowData: UserSummaryType) => (
              <label>
                {rowData.firstName} {rowData.lastName}
              </label>
            )}
          />

          <Column
            expander={false}
            header="ת.ז"
            field={'nationalId'}
            style={columnStyle}
          />

          <Column
            expander={false}
            header={'נוכחות'}
            field={'firstName'}
            style={columnStyle}
            body={(rowData: UserSummaryType) => (
              <Checkbox
                checked={
                  studentClassRegistrations[rowData.id]?.attended ===
                  AttendanceType.Attended
                }
                onChange={(e) =>
                  updateStudentAttendance(rowData.id, AttendanceType.Attended)
                }
              />
            )}
          />

          <Column
            expander={false}
            header={'איחור'}
            field={'firstName'}
            style={columnStyle}
            body={(rowData: UserSummaryType) => (
              <Checkbox
                checked={
                  studentClassRegistrations[rowData.id]?.attended ===
                  AttendanceType.Late
                }
                onChange={(e) =>
                  updateStudentAttendance(rowData.id, AttendanceType.Late)
                }
              />
            )}
          />

          <Column
            expander={false}
            header={'חיסור'}
            field={'firstName'}
            style={columnStyle}
            body={(rowData: UserSummaryType) => (
              <Checkbox
                checked={
                  studentClassRegistrations[rowData.id]?.attended ===
                  AttendanceType.Missed
                }
                onChange={(e) =>
                  updateStudentAttendance(rowData.id, AttendanceType.Missed)
                }
              />
            )}
          />
        </DataTable>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '16px',
          }}
        >
          <Button
            style={{ height: '30px' }}
            label={'אישור'}
            loading={loading}
            disabled={!hasChanges}
            tooltip={hasChanges ? 'לחץ לאישור' : 'יש לבצע שינוי בנוכחות'}
            tooltipOptions={{ showOnDisabled: true }}
            onClick={handleOnApprove}
          />
        </div>
      </div>
    </div>
  );
};
